import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { IEditAccountData } from '@App/redux/slices/user/types'
import ProxyServiceConnection from '@App/sockets/proxy_connection_new'
import SocketMiddleware from '@App/sockets/socket_middleware'
import { Events, SocketState } from '@App/sockets/types'

import {setPriceAlerts} from "@Redux/slices/trade/alerts/PriceAlertSlice";
import {setOrders} from "@Redux/slices/trade/trades/OrdersSlice"
import {setTrades} from "@Redux/slices/trade/trades/TradesSlice"
import { ICreateWatchlist, IUpdateWatchlist } from "@Redux/slices/user/types";
import {
  asyncLogout,
  handleWalletRemoval,
  removeWallet,
  setCurrWallet,
  setUserInfo,
  setWallet
} from '@Redux/slices/user/UserSlice'
import { createWatchlists, deleteWatchlists, setWatchlists } from '@Redux/slices/watchlist/slice'

import {
  ICreatePriceAlertRequest,
  IDeletePriceAlertRequest,
  ISimpleResponse,
  IUpdatePriceAlertRequest
} from '@Interfaces/CommonInterfaces'

import type { IProxyServiceConnection, IProxyServiceMessage } from '@Redux/interfaces'
import type { PayloadAction } from '@reduxjs/toolkit'
import {store} from "@Redux/store";

const initialState: IProxyServiceConnection = {
  socket_status: SocketState.Closed,
}

let processMessageFunc: any

const getProcessMessageFunc = (dispatch: any): any => {
  if (processMessageFunc) {
    return processMessageFunc
  }

  processMessageFunc = (message: IProxyServiceMessage) => {
    switch (message.event) {
      case Events.GetUserEvent: {
        if (message?.payload?.user) {
          dispatch(setUserInfo(message.payload.user))
        }
        break
      }

      case Events.GetUserWalletEvent: {
        const wallet = message?.payload?.wallet
        const walletId = Number(wallet?.id ?? message?.payload?.wallet_id) || undefined;

        if (wallet) {
          dispatch(setWallet(wallet))
        } else if (walletId) {
          dispatch(handleWalletRemoval(walletId))
        }

        if (walletId && !wallet) {
          SocketMiddleware.deleteSubscriber(
            [ProxyServiceConnection.token, JSON.stringify({ wallet_id: walletId })],
            Events.GetUserWalletEvent,
            ProxyServiceConnection,
          )

          ProxyServiceConnection.wallet_ids =
            ProxyServiceConnection.wallet_ids.filter(id => id !== walletId)
        }

        break
      }
      case Events.GetUserOrdersEvent: {
        if (message.payload.wallet?.id) {
          message.payload.orders && dispatch(setOrders({
            orders: message.payload.orders,
            accountId: message.payload.wallet.id
          }))

          message?.payload?.trades && dispatch(setTrades({
            trades: message.payload.trades,
            accountId: message.payload.wallet.id
          }))
        }

        break
      }
      case Events.UpdateToken: {
        if (!message.status) {
          console.log('work')
          dispatch(asyncLogout())

          // ProxyServiceConnection.unsubscribe(getProcessMessageFunc(dispatch))
          // ProxyServiceConnection.closeSocket()
        }
        break
      }

      case Events.SocketStatusConnection:
        dispatch(
          setProxyStatusAsync({
            socket_status: message.status,
          }),
        )
        break

      case Events.GetWatchlists:
        message?.payload?.watchlists && dispatch(setWatchlists(message.payload.watchlists))
        break
      case Events.CreateWatchlist:
        message?.payload?.watchlist_key && dispatch(createWatchlists(message.payload.watchlist_key))
        break
      case Events.DeleteWatchlist:
        message?.payload?.watchlist_key && dispatch(deleteWatchlists(message.payload.watchlist_key))
        break
      case Events.DeleteAccount:
        break
      case Events.GetUserPriceAlerts:
        message.payload?.alerts && dispatch(setPriceAlerts({
          alerts: message.payload.alerts
        }))
        break
    }
  }

  return processMessageFunc
}

export const setConfigProxyServiceConnection = createAsyncThunk(
  'socket/setProxyServiceConnectionConfig',
  async (path: string) => {
    ProxyServiceConnection.setApiUrl(path)
  },
)

export const setTokenProxyServiceConnection = createAsyncThunk(
  'socket/setProxyServiceConnectionToken',
  async (token: string, { dispatch }) => {
    SocketMiddleware.addSubscriber(
      [token],
      Events.GetUserEvent,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const getNonExistWallets = (wallet_ids: number[]) =>
  wallet_ids.filter((wallet_id) => !ProxyServiceConnection.wallet_ids.includes(wallet_id)) ?? []

export const setUserWalletConnection = createAsyncThunk(
  'socket/setUserWalletConnection',
  async (wallet_id: number, { dispatch }) => {
    SocketMiddleware.addSubscriber(
      [ProxyServiceConnection.token, JSON.stringify({ wallet_id: wallet_id })],
      Events.GetUserWalletEvent,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const setUserOrdersConnection = createAsyncThunk<void, { accountId: number, isEdit?: boolean }>(
  'socket/setUserOrdersConnection',
  async ({accountId, isEdit = true}, {dispatch}) => {
    if (!ProxyServiceConnection.token || ProxyServiceConnection.account_ids.includes(accountId)) return;

    if (isEdit && ProxyServiceConnection?.account_ids?.length) {
      ProxyServiceConnection.account_ids.forEach(account_id => {
        SocketMiddleware.deleteSubscriber([ProxyServiceConnection.token, JSON.stringify({account_id: account_id})], Events.GetUserOrdersEvent, ProxyServiceConnection)
      })
    }

    SocketMiddleware.addSubscriber(
      [ProxyServiceConnection.token, JSON.stringify({account_id: accountId})],
      Events.GetUserOrdersEvent,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch),
    )
  }
)

export const sendDeleteAccountConnection = createAsyncThunk(
  'socket/sendDeleteAccountConnection',
  async (accountId: number, { dispatch }) => {
    ProxyServiceConnection.request(
      [
        ProxyServiceConnection.token,
        JSON.stringify({ account_id: accountId })
      ],
      Events.DeleteAccount,
      getProcessMessageFunc(dispatch),
    )
  },
)


export const setUserPriceAlertsConnection = createAsyncThunk<void, { userId: number, isEdit?: boolean }>(
  'socket/setUserPriceAlertsConnection',
  async ({userId, isEdit = true}, {dispatch}) => {
    if (!ProxyServiceConnection.token) return;
    if (isEdit) {
      SocketMiddleware.deleteSubscriber([ProxyServiceConnection.token, JSON.stringify({user_id: userId})], Events.GetUserPriceAlerts, ProxyServiceConnection)
    }
    SocketMiddleware.addSubscriber(
      [ProxyServiceConnection.token, JSON.stringify({user_id: userId})],
      Events.GetUserPriceAlerts,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch),
    )
  }
)

export const unsubscribeUserOrders = createAsyncThunk(
  'socket/unsubscribeUserOrders',
  async (accountId: number) => {
    SocketMiddleware.deleteSubscriber([ProxyServiceConnection.token, JSON.stringify({account_id: accountId})], Events.GetUserOrdersEvent, ProxyServiceConnection)
  }
)

export const setWatchlistsConnection = createAsyncThunk(
  'socket/setWatchlistsConnection',
  async (token: string, { dispatch }) => {
    SocketMiddleware.addSubscriber(
      [token],
      Events.GetWatchlists,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const sendCreateWatchlistConnection = createAsyncThunk(
  'socket/sendCreateWatchlistConnection',
  async (payload: ICreateWatchlist, { dispatch }) => {
    ProxyServiceConnection.request(
      [ProxyServiceConnection.token, JSON.stringify(payload)],
      Events.CreateWatchlist,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const sendUpdateWatchlistConnection = createAsyncThunk(
  'socket/sendUpdateWatchlistConnection',
  async (payload: IUpdateWatchlist, { dispatch }) => {
    ProxyServiceConnection.request(
      [ProxyServiceConnection.token, JSON.stringify(payload)],
      Events.UpdateWatchlist,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const sendDeleteWatchlistConnection = createAsyncThunk(
  'socket/sendDeleteWatchlistConnection',
  async (watchlistKey: string, { dispatch }) => {
    ProxyServiceConnection.request(
      [ProxyServiceConnection.token, JSON.stringify({key: watchlistKey})],
      Events.DeleteWatchlist,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const sendUpdateWatchlistOrdersConnection = createAsyncThunk(
  'socket/sendUpdateWatchlistOrdersConnection',
  async (watchlistKeys: string[], { dispatch }) => {
    ProxyServiceConnection.request(
      [ProxyServiceConnection.token, JSON.stringify({keys: watchlistKeys})],
      Events.UpdateWatchlistOrders,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const sendCreatePriceAlert = createAsyncThunk(
  'socket/sendCreatePriceAlertConnection',
  async (payload: ICreatePriceAlertRequest, { dispatch }) => {
    ProxyServiceConnection.request(
      [ProxyServiceConnection.token, JSON.stringify(payload)],
      Events.CreatePriceAlerts,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const sendUpdatePriceAlert = createAsyncThunk(
  'socket/sendUpdatePriceAlertConnection',
  async (payload: IUpdatePriceAlertRequest, { dispatch }) => {
    ProxyServiceConnection.request(
      [ProxyServiceConnection.token, JSON.stringify(payload)],
      Events.UpdatePriceAlerts,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const sendDeletePriceAlert = createAsyncThunk(
  'socket/sendDeletePriceAlertConnection',
  async (payload: IDeletePriceAlertRequest, { dispatch }) => {
    ProxyServiceConnection.request(
      [ProxyServiceConnection.token, JSON.stringify(payload)],
      Events.DeletePriceAlerts,
      getProcessMessageFunc(dispatch),
    )
  },
)

export const updateTradingSettingWallet = createAsyncThunk(
  'socket/updateTradingSettings',
  async (payload: { wallet_id: string; data: IEditAccountData }, { dispatch }) => {

    let mirror_trade = false
    if (payload.data.mirror_trade == 1) {
      mirror_trade = true
    }


    const data = {
      id: payload.wallet_id,
      mirror_trade: mirror_trade,
      trading_settings: payload.data.trading_settings
    }

    SocketMiddleware.addSubscriber(
      [ProxyServiceConnection.token, JSON.stringify(data)],
      Events.UpdateTradingSettingWallet,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch)
    )
  }
)

export const updateTokenProxyServiceConnection = createAsyncThunk(
  'socket/setProxyServiceConnectionToken',
  async (token: string, { dispatch }) => {
    const old_token = ProxyServiceConnection.token

    if (ProxyServiceConnection.wallet_ids) {
      ProxyServiceConnection.wallet_ids?.map((id) =>
        SocketMiddleware.deleteSubscriber(
          [old_token, JSON.stringify({ wallet_id: id })],
          Events.GetUserWalletEvent,
          ProxyServiceConnection,
        ),
      )
    }

    SocketMiddleware.deleteSubscriber([old_token], Events.UpdateToken, ProxyServiceConnection)
    SocketMiddleware.addSubscriber([token], Events.UpdateToken, ProxyServiceConnection, getProcessMessageFunc(dispatch))

    SocketMiddleware.deleteSubscriber([old_token], Events.GetUserEvent, ProxyServiceConnection)
    SocketMiddleware.addSubscriber(
      [token],
      Events.GetUserEvent,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch),
    )

    if (ProxyServiceConnection?.account_ids?.length) {
      ProxyServiceConnection.account_ids.forEach(account_id => {
        const accountJson = JSON.stringify({ account_id: account_id })
        SocketMiddleware.deleteSubscriber([old_token, accountJson], Events.GetUserOrdersEvent, ProxyServiceConnection)
        SocketMiddleware.addSubscriber(
          [token, accountJson],
          Events.GetUserOrdersEvent,
          ProxyServiceConnection,
          getProcessMessageFunc(dispatch),
        )
      })
    }

    SocketMiddleware.deleteSubscriber([old_token], Events.GetWatchlists, ProxyServiceConnection)
    SocketMiddleware.addSubscriber(
      [token],
      Events.GetWatchlists,
      ProxyServiceConnection,
      getProcessMessageFunc(dispatch),
    )

    // ProxyServiceConnection.updateToken(token)
    // ProxyServiceConnection.updateTokenActions(token)
  },
)

export const setProxyStatusAsync = createAsyncThunk('socket/setProxyStatusAsync', async (data: any) => {
  return data
})

export const setWalletsAsync = createAsyncThunk('socket/setWalletsAsync', async (data: any) => {
  return data
})

export const setRateAsync = createAsyncThunk('socket/setRateAsync', async (data: any) => {
  return data
})

export const setMessagesAsync = createAsyncThunk('socket/setMessagesAsync', async (data: any) => {
  return data
})

// export const subscribeToProxyService = createAsyncThunk('socket/subscribeToToProxyService', async (_, { dispatch }) => {
//   ProxyServiceConnection.subscribe(getProcessMessageFunc(dispatch))
//   return null
// })

// export const unsubscribeToToProxyService = createAsyncThunk(
//   'socket/unsubscribeToToProxyService',
//   async (_, { dispatch }) => {
//     ProxyServiceConnection.unsubscribe(getProcessMessageFunc(dispatch))
//     return null
//   },
// )

export const closeProxyServiceSocket = createAsyncThunk('socket/closeProxyServiceSocket', async () => {
  if (ProxyServiceConnection.wallet_ids) {
    ProxyServiceConnection.wallet_ids?.map((id) =>
      SocketMiddleware.deleteSubscriber(
        [ProxyServiceConnection.token, JSON.stringify({ wallet_id: id })],
        Events.GetUserWalletEvent,
        ProxyServiceConnection,
      ),
    )
  }
  if (ProxyServiceConnection?.account_ids?.length) {
    ProxyServiceConnection.account_ids.forEach(account_id => {
      SocketMiddleware.deleteSubscriber([ProxyServiceConnection.token, JSON.stringify({ account_id: account_id })], Events.GetUserOrdersEvent, ProxyServiceConnection)
    })
  }
  SocketMiddleware.deleteSubscriber([ProxyServiceConnection.token], Events.GetUserEvent, ProxyServiceConnection)
  SocketMiddleware.deleteSubscriber([ProxyServiceConnection.token], Events.UpdateToken, ProxyServiceConnection)
  SocketMiddleware.deleteSubscriber([ProxyServiceConnection.token], Events.GetUserPriceAlerts, ProxyServiceConnection)
  ProxyServiceConnection.forceCloseSocket()
  return null
})

export const ProxyServiceConnectionSlice = createSlice({
  name: 'proxy_service',
  initialState,
  reducers: {
    setMessages: (state, action: PayloadAction<ISimpleResponse>) => {
      state.message = action.payload.message
      state.status = action.payload.status
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setProxyStatusAsync.fulfilled, (state, action) => {
      state.socket_status = action.payload.socket_status
    })
  },
})

export default ProxyServiceConnectionSlice.reducer
